import { Box, Typography } from '@mui/material';

import { formatCardDate, type PeCardView } from '../programEvaluationCard';
import { REPORTS } from '../tokens';
import { PendingFooter, ReportCardButton } from './ReportCard';

function Note({ text, tone = 'muted' }: { text: string; tone?: 'muted' | 'error' }) {
  return (
    <Typography
      sx={{
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 500,
        color: tone === 'error' ? REPORTS.error : REPORTS.muted,
      }}
    >
      {text}
    </Typography>
  );
}

const PREVIEW_NAME = 'Preview Program Evaluation';
const VIEW_NAME = 'View Program Evaluation';

export function ProgramEvaluationFooter({
  view,
  onPreview,
  onGenerate,
  onRetry,
  onRegenerate,
  onView,
  onRetryStatus,
}: {
  view: PeCardView;
  onPreview: () => void;
  onGenerate: () => void;
  onRetry: () => void;
  onRegenerate: () => void;
  onView: () => void;
  onRetryStatus: () => void;
}) {
  if (view.kind === 'pending') return <PendingFooter />;
  const split = view.kind !== 'preview' && view.kind !== 'generate';
  // Stale stacks the note above the buttons: two buttons plus a dated note
  // don't fit on one row at the tile's narrower widths.
  const stacked = view.kind === 'stale';
  return (
    <Box
      sx={{
        mt: 'auto',
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        justifyContent: split ? 'space-between' : 'flex-end',
        alignItems: stacked ? 'stretch' : 'center',
        gap: '8px',
      }}
    >
      {view.kind === 'preview' && (
        <ReportCardButton label="Preview" name={PREVIEW_NAME} variant="outlined" onClick={onPreview} />
      )}
      {view.kind === 'generate' && <ReportCardButton label="Generate" variant="contained" onClick={onGenerate} />}
      {view.kind === 'unavailable' && (
        <>
          <Note text="Couldn’t load right now" />
          <ReportCardButton label="Retry" variant="text" onClick={onRetryStatus} />
        </>
      )}
      {view.kind === 'generating' && (
        <>
          <Note text="Takes a few minutes" />
          <ReportCardButton label="Generating…" variant="outlined" disabled />
        </>
      )}
      {view.kind === 'failed' && (
        <>
          <Note text="Couldn’t generate" tone="error" />
          <ReportCardButton label="Try again" variant="contained" onClick={onRetry} />
        </>
      )}
      {view.kind === 'generated' && (
        <>
          <Note text={`Generated ${formatCardDate(view.generatedAt)}`} />
          <ReportCardButton label="View" name={VIEW_NAME} variant="outlined" onClick={onView} />
        </>
      )}
      {view.kind === 'stale' && (
        <>
          <Note text={`New data since ${formatCardDate(view.generatedAt)}`} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px' }}>
            <ReportCardButton label="Regenerate" variant="text" onClick={onRegenerate} />
            <ReportCardButton label="View" name={VIEW_NAME} variant="outlined" onClick={onView} />
          </Box>
        </>
      )}
    </Box>
  );
}
